import type { DoneCriterion, ProjectLifecycle, SpecContent, WorkOrderState } from "@szyos/contracts";
import { DomainError } from "./errors";
import { isOrderOpen, type OrderFacts } from "./portfolio";

/**
 * Fertigstellen vor Anfangen (docs/10, UX-10): Fokus-Grenze, „Fertig heißt“, genau ein nächster Schritt je Projekt,
 * Wochenrückblick, Bestandsaufnahme und kleine Aufträge. Alles leitet sich aus echten Daten ab, ohne Prozentwerte.
 */

export const MAX_FOCUS_PROJECTS = 3;
export const MAX_DONE_CRITERIA = 7;
/** Weniger als drei Kriterien sind erlaubt, aber die Oberfläche empfiehlt mindestens drei. */
export const MIN_RECOMMENDED_DONE_CRITERIA = 3;

/**
 * Prüft, ob ein Projekt in den Fokus darf, und liefert das Projekt, das dafür pausiert (oder null).
 * `current` sind die Projekte, die gerade im Fokus sind.
 */
export function planFocus(
  current: readonly string[],
  projectId: string,
  lifecycle: ProjectLifecycle,
  pauseProjectId?: string,
): string | null {
  if (lifecycle === "closed") {
    throw new DomainError("CONFLICT", "Abgeschlossene Projekte kommen nicht in den Fokus", { projectId });
  }
  if (current.includes(projectId) || current.length < MAX_FOCUS_PROJECTS) return null;
  if (!pauseProjectId) {
    throw new DomainError(
      "CONFLICT",
      `Im Fokus sind schon ${MAX_FOCUS_PROJECTS} Projekte. Wähle eins, das dafür pausiert.`,
      { reason: "FOCUS_FULL", focus: [...current] },
    );
  }
  if (!current.includes(pauseProjectId)) {
    throw new DomainError("CONFLICT", "Das gewählte Projekt ist nicht im Fokus", { pauseProjectId });
  }
  return pauseProjectId;
}

/**
 * Übernimmt neue Kriterien und behält den Zeitpunkt, an dem ein unverändertes Kriterium erfüllt wurde.
 * Doppelte Kriterien sind ein Fehler, weil sie den Fortschritt verfälschen.
 */
export function mergeDoneCriteria(
  previous: readonly DoneCriterion[],
  next: readonly { text: string; met: boolean }[],
  now: string,
): DoneCriterion[] {
  if (next.length > MAX_DONE_CRITERIA) {
    throw new DomainError("CONFLICT", `Höchstens ${MAX_DONE_CRITERIA} Kriterien`, { count: next.length });
  }
  const seen = new Set<string>();
  return next.map(({ text, met }) => {
    const key = text.trim().toLocaleLowerCase("de-DE");
    if (seen.has(key)) throw new DomainError("CONFLICT", `Kriterium doppelt: ${text}`, { text });
    seen.add(key);
    const before = previous.find((c) => c.text === text);
    return { text, met, metAt: met ? (before?.met ? before.metAt : now) : null };
  });
}

export function doneProgress(criteria: readonly DoneCriterion[]): { met: number; total: number } {
  return { met: criteria.filter((c) => c.met).length, total: criteria.length };
}

export function isProjectDone(criteria: readonly DoneCriterion[]): boolean {
  return criteria.length > 0 && criteria.every((c) => c.met);
}

export interface StepOrder extends OrderFacts {
  id: string;
  title: string;
}

export interface StepProject {
  lifecycle: ProjectLifecycle;
  focus: boolean;
  doneCriteria: readonly DoneCriterion[];
}

export type NextStep =
  | { kind: "order" | "wait"; label: string; orderId: string; orderTitle: string }
  | { kind: "focus" | "define_done" | "finish" | "none"; label: string }
  | { kind: "criterion"; label: string; criterion: string };

/** Was ein Mensch bei einem Auftrag in diesem Zustand tun muss; Reihenfolge = Dringlichkeit. */
const HUMAN_STEP: Partial<Record<WorkOrderState, { rank: number; label: string }>> = {
  WAITING_HUMAN: { rank: 0, label: "Entscheidung treffen" },
  BLOCKED: { rank: 1, label: "Blockade lösen" },
  SUSPECT: { rank: 1, label: "Auffälligen Lauf prüfen" },
  REVIEW_READY: { rank: 2, label: "Ergebnis prüfen" },
  RELEASE_APPROVAL: { rank: 3, label: "Ausliefern" },
  PLAN_REVIEW: { rank: 4, label: "Plan freigeben" },
  SPEC_REVIEW: { rank: 5, label: "Briefing freigeben" },
  READY: { rank: 6, label: "Auftrag starten" },
  DRAFT: { rank: 7, label: "Briefing schreiben" },
};

function humanStep(o: StepOrder): { rank: number; label: string } | null {
  if (o.openDecisions > 0) return { rank: 0, label: "Entscheidung treffen" };
  return HUMAN_STEP[o.state] ?? null;
}

/**
 * Genau ein nächster Schritt je Projekt (UX-10). Zuerst, was ein Mensch bei einem offenen Auftrag tun muss
 * (am längsten wartend zuerst), dann laufende Arbeit, dann der Weg zu „Fertig heißt“.
 */
export function nextStep(project: StepProject, orders: readonly StepOrder[]): NextStep {
  const open = orders.filter((o) => isOrderOpen(o.state));
  const waiting = open
    .map((o) => ({ o, step: humanStep(o) }))
    .filter((x): x is { o: StepOrder; step: { rank: number; label: string } } => x.step !== null)
    .sort((a, b) => a.step.rank - b.step.rank || a.o.updatedAt.localeCompare(b.o.updatedAt));
  const first = waiting[0];
  if (first)
    return { kind: "order", label: first.step.label, orderId: first.o.id, orderTitle: first.o.title };
  const running = open[0];
  if (running)
    return {
      kind: "wait",
      label: "Läuft, du musst nichts tun",
      orderId: running.id,
      orderTitle: running.title,
    };
  if (project.lifecycle === "closed") return { kind: "none", label: "Abgeschlossen" };
  if (project.lifecycle === "operating" && !project.focus)
    return { kind: "none", label: "Im Betrieb, nichts offen" };
  if (!project.focus) return { kind: "focus", label: "Geparkt: in den Fokus nehmen" };
  if (project.doneCriteria.length === 0) return { kind: "define_done", label: "„Fertig heißt“ festlegen" };
  const unmet = project.doneCriteria.find((c) => !c.met);
  if (!unmet) return { kind: "finish", label: "Alles erfüllt: abschließen oder in Betrieb nehmen" };
  return { kind: "criterion", label: "Auftrag anlegen für", criterion: unmet.text };
}

export const REVIEW_INTERVAL_DAYS = 7;
/** Ein Fokusprojekt ohne Bewegung seit so vielen Tagen gilt als hängend. */
export const STALL_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

const daysSince = (iso: string, now: Date) => (now.getTime() - new Date(iso).getTime()) / DAY_MS;

export function weeklyReviewDue(lastReviewAt: string | null, now: Date): boolean {
  return lastReviewAt === null || daysSince(lastReviewAt, now) >= REVIEW_INTERVAL_DAYS;
}

export function isStalled(lastActivityAt: string, now: Date): boolean {
  return daysSince(lastActivityAt, now) >= STALL_DAYS;
}

export interface ReviewProject {
  id: string;
  title: string;
  focus: boolean;
  lifecycle: ProjectLifecycle;
  doneCriteria: readonly DoneCriterion[];
  lastActivityAt: string;
}

export interface ReviewOrder {
  id: string;
  title: string;
  projectId: string;
  state: WorkOrderState;
  updatedAt: string;
}

export interface WeeklyReview {
  /** In den letzten sieben Tagen veröffentlichte Aufträge. */
  released: ReviewOrder[];
  /** In den letzten sieben Tagen erfüllte Kriterien. */
  metCriteria: { projectId: string; projectTitle: string; text: string }[];
  focus: ReviewProject[];
  /** Fokusprojekte ohne Bewegung seit mindestens STALL_DAYS Tagen. */
  stalled: ReviewProject[];
}

/** Inhalt des Wochenrückblicks: was fertig wurde, was im Fokus ist und was hängt. */
export function weeklyReview(
  projects: readonly ReviewProject[],
  orders: readonly ReviewOrder[],
  now: Date,
): WeeklyReview {
  const thisWeek = (iso: string | null) => iso !== null && daysSince(iso, now) < REVIEW_INTERVAL_DAYS;
  const focus = projects.filter((p) => p.focus && p.lifecycle !== "closed");
  return {
    released: orders.filter((o) => o.state === "RELEASED" && thisWeek(o.updatedAt)),
    metCriteria: projects.flatMap((p) =>
      p.doneCriteria
        .filter((c) => c.met && thisWeek(c.metAt))
        .map((c) => ({ projectId: p.id, projectTitle: p.title, text: c.text })),
    ),
    focus,
    stalled: focus.filter((p) => isStalled(p.lastActivityAt, now)),
  };
}

/** Kleine Aufträge (UX-10): mehr Muss-Kriterien oder Aufgaben deuten auf einen Auftrag, der geteilt werden sollte. */
export const SMALL_ORDER_MAX_MUST_HAVES = 5;
export const SMALL_ORDER_MAX_ITEMS = 6;
/**
 * Bis zu so vielen Muss-Funktionen plant szyOS eine einzige Code-Aufgabe, die ihre Tests selbst schreibt und
 * ausführt, ohne eigene Recherche-, Test- oder Doku-Aufgabe (Bewährungsphase, docs/21): jeder Lauf startet kalt
 * und liest das Repo neu, getrennte kleine Läufe kosten deshalb mehr, als sie absichern.
 */
export const SINGLE_RUN_MAX_MUST_HAVES = 3;

/** Reicht für diesen Auftrag eine einzige Code-Aufgabe? */
export function isSingleRunSpec(spec: Pick<SpecContent, "mustHaves">): boolean {
  return spec.mustHaves.length <= SINGLE_RUN_MAX_MUST_HAVES;
}

export function isLargeSpec(spec: Pick<SpecContent, "mustHaves">): boolean {
  return spec.mustHaves.length > SMALL_ORDER_MAX_MUST_HAVES;
}

/** Vorsatz für Muss-Funktionen, die beim Teilen in einen eigenen Auftrag wandern. */
export const MOVED_PREFIX = "Kommt in einen eigenen Auftrag: ";
/** Vorsatz für Prüfpunkte, die szyOS erzeugt, wenn einem Teil kein Akzeptanzkriterium zugeordnet werden kann. */
export const GENERATED_CHECK_PREFIX = "Umgesetzt und geprüft: ";
const MAX_SPEC_LINE = 500;
const MAX_SPEC_LIST = 50;

const STOP_WORDS = new Set([
  "aber",
  "alle",
  "allen",
  "auch",
  "dass",
  "dann",
  "denn",
  "der",
  "die",
  "das",
  "durch",
  "eine",
  "einen",
  "einem",
  "einer",
  "eines",
  "jede",
  "jeder",
  "jedes",
  "kein",
  "keine",
  "nach",
  "nicht",
  "noch",
  "oder",
  "ohne",
  "sind",
  "soll",
  "statt",
  "über",
  "unter",
  "und",
  "beim",
  "wird",
  "werden",
  "wenn",
  "zwischen",
  "sich",
  "mehr",
]);

/** Wortstämme eines Textes: klein, ohne Füllwörter, grob ohne deutsche Endungen. */
function stems(text: string): Set<string> {
  const words = text.toLocaleLowerCase("de-DE").match(/[a-zäöüß0-9]+/g) ?? [];
  const out = new Set<string>();
  for (const w of words) {
    if (w.length < 4 || STOP_WORDS.has(w)) continue;
    const stem = w.replace(/(ungen|ung|en|er|es|e|n|s)$/, "");
    out.add(stem.length >= 4 ? stem : w);
  }
  return out;
}

/** Verwandte Wörter: gleicher Stamm, Teil eines zusammengesetzten Wortes („Komponente“ in „Grundkomponenten“). */
function related(a: string, b: string): boolean {
  if (a === b) return true;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  if (short.length >= 6 && long.includes(short)) return true;
  // Gleicher Wortanfang ab fünf Buchstaben: „Prüfskripte“ und „Prüfungen“, „Kontrast“ und „Kontrastprüfung“.
  let common = 0;
  while (common < short.length && short[common] === long[common]) common++;
  return common >= 5;
}

export interface CheckAssignment {
  /** 1 = bleibt im bestehenden Auftrag, 2 = wandert in den neuen. */
  order: 1 | 2;
  /** false, wenn die Zuordnung knapp war; die Oberfläche bittet dann um einen Blick. */
  sure: boolean;
}

/**
 * Ordnet beim Teilen jedes Akzeptanzkriterium dem Auftrag zu, zu dessen Muss-Funktionen es am besten passt.
 * Gemeinsame Wortstämme zählen, seltene stärker als solche, die in vielen Funktionen vorkommen. Gibt es gleich viele
 * Funktionen wie Kriterien, gehört Kriterium n zu Funktion n (so sind Briefings meist aufgebaut); dann ist die
 * Zuordnung sicher, wenn die Wörter nicht klar dagegen sprechen. Sonst entscheidet der Wortvergleich, und knappe
 * Fälle bleiben im bestehenden Auftrag und gelten als unsicher.
 */
export function assignChecks(
  spec: Pick<SpecContent, "mustHaves" | "acceptance">,
  movedMust: readonly number[],
): CheckAssignment[] {
  const moved = new Set(movedMust);
  const must = spec.mustHaves.map((m) => [...stems(m)]);
  const spread = (w: string) => Math.max(1, must.filter((m) => m.some((x) => related(w, x))).length);
  const score = (words: string[], m: string[]) =>
    words.reduce((sum, w) => (m.some((x) => related(w, x)) ? sum + 1 / spread(w) : sum), 0);
  const paired = spec.mustHaves.length === spec.acceptance.length;
  return spec.acceptance.map((text, j) => {
    const words = [...stems(text)];
    let stay = 0;
    let go = 0;
    must.forEach((m, i) => {
      if (moved.has(i)) go = Math.max(go, score(words, m));
      else stay = Math.max(stay, score(words, m));
    });
    const byWords: 1 | 2 = go > stay ? 2 : 1;
    const clear = Math.abs(go - stay) >= 0.75;
    if (paired) {
      const byPosition: 1 | 2 = moved.has(j) ? 2 : 1;
      if (byWords === byPosition || !clear)
        return { order: byPosition, sure: byWords === byPosition || go === stay };
      return { order: byWords, sure: false };
    }
    return { order: byWords, sure: clear };
  });
}

/** Nummern der Kriterien, die nach `assignChecks` in den neuen Auftrag wandern. */
export function assignAcceptance(
  spec: Pick<SpecContent, "mustHaves" | "acceptance">,
  movedMust: readonly number[],
): number[] {
  return assignChecks(spec, movedMust).flatMap((a, j) => (a.order === 2 ? [j] : []));
}

function checkIndices(label: string, list: readonly string[], indices: readonly number[]): Set<number> {
  const picked = new Set(indices);
  if (picked.size !== indices.length) throw new DomainError("CONFLICT", `${label}: Auswahl doppelt`);
  for (const i of picked) {
    if (!Number.isInteger(i) || i < 0 || i >= list.length) {
      throw new DomainError("CONFLICT", `${label}: Auswahl ${i} gibt es nicht`, { index: i });
    }
  }
  return picked;
}

const generatedChecks = (mustHaves: readonly string[]) =>
  mustHaves.map((m) => `${GENERATED_CHECK_PREFIX}${m}`.slice(0, MAX_SPEC_LINE)).slice(0, MAX_SPEC_LIST);

/**
 * Teilt ein Briefing (UX-10, kleine Aufträge) in Auftrag 1 (bleibt) und Auftrag 2 (neu). Der Mensch ordnet nur die
 * Muss-Funktionen zu; die Akzeptanzkriterien ordnet `assignAcceptance` zu, falls keine Auswahl mitkommt. Bekommt ein
 * Teil kein Kriterium, entstehen Prüfpunkte aus seinen Funktionen. Problem, Zielgruppe, Gestaltungshinweise und
 * Nicht-Ziele gelten für beide; im bleibenden Briefing stehen die verschobenen Funktionen als Nicht-Ziel.
 */
export function splitSpec(
  spec: SpecContent,
  move: { mustHaves: readonly number[]; acceptance?: readonly number[] | undefined },
): { keep: SpecContent; moved: SpecContent; acceptanceMoved: number[] } {
  const must = checkIndices("Funktionen", spec.mustHaves, move.mustHaves);
  if (must.size === 0) throw new DomainError("CONFLICT", "Mindestens eine Funktion muss in Auftrag 2");
  if (must.size === spec.mustHaves.length) {
    throw new DomainError("CONFLICT", "Mindestens eine Funktion muss in Auftrag 1 bleiben");
  }
  const acc = checkIndices(
    "Prüfpunkte",
    spec.acceptance,
    move.acceptance ?? assignAcceptance(spec, move.mustHaves),
  );
  const keptMust = spec.mustHaves.filter((_, i) => !must.has(i));
  const movedMust = spec.mustHaves.filter((_, i) => must.has(i));
  const keptAcc = spec.acceptance.filter((_, i) => !acc.has(i));
  const movedAcc = spec.acceptance.filter((_, i) => acc.has(i));
  const notHere = movedMust.map((m) => `${MOVED_PREFIX}${m}`.slice(0, MAX_SPEC_LINE));
  return {
    keep: {
      ...spec,
      mustHaves: keptMust,
      acceptance: keptAcc.length > 0 ? keptAcc : generatedChecks(keptMust),
      nonGoals: [...spec.nonGoals, ...notHere].slice(0, MAX_SPEC_LIST),
    },
    moved: {
      ...spec,
      mustHaves: movedMust,
      acceptance: movedAcc.length > 0 ? movedAcc : generatedChecks(movedMust),
      // Hinweise aus einer früheren Teilung gehören nicht in den neuen Auftrag.
      nonGoals: spec.nonGoals.filter((n) => !n.startsWith(MOVED_PREFIX)),
    },
    acceptanceMoved: [...acc].sort((a, b) => a - b),
  };
}

/** Was der Owner in der Sammelfreigabe bei einem Auftrag tun kann; null, wenn nichts auf ihn wartet. */
export type ApprovalKind = "spec" | "plan" | "start";

export function approvalKind(
  state: WorkOrderState,
  plan: { submitted: boolean; proposal: boolean },
): ApprovalKind | null {
  if (state === "SPEC_REVIEW") return "spec";
  if (state === "PLAN_REVIEW" && (plan.submitted || plan.proposal)) return "plan";
  if (state === "READY") return "start";
  return null;
}

export const STOCKTAKE_TITLE = "Bestandsaufnahme";

export function isStocktakeOrder(o: { kind: string; title: string }): boolean {
  return o.kind === "audit" && o.title === STOCKTAKE_TITLE;
}

/**
 * Briefing für die Bestandsaufnahme eines angefangenen Projekts. Der Lauf ändert keinen Code; er liefert Stand,
 * Vorschlag für „Fertig heißt“, „Später“, einen nächsten Schritt und eine Empfehlung. Entscheiden tut der Owner.
 */
export function stocktakeSpec(project: { title: string; goal: string }): SpecContent {
  return {
    problem: `Das Projekt „${project.title}“ ist angefangen, aber sein Stand ist unklar. Ziel laut szyOS: ${project.goal} Vor weiterer Arbeit braucht der Owner einen belegten Überblick und eine klare Grenze, was „fertig“ heißt.`,
    audience:
      "Der Owner, der entscheidet, ob das Projekt fertiggestellt, geparkt oder abgeschlossen wird, und szyOS, das die Kriterien danach als „Fertig heißt“ übernimmt.",
    mustHaves: [
      "Stand erfassen: was gebaut ist, was läuft, was fehlt oder kaputt ist; jede Aussage mit Beleg (Datei, Befehl, Testergebnis).",
      "Vorschlag „Fertig heißt“: drei bis sieben prüfbare Kriterien für die erste fertige Version.",
      "Alles, was darüber hinausgeht, als Liste „Später“.",
      "Genau ein nächster Schritt, der in einem kleinen Auftrag erledigt werden kann.",
      "Empfehlung fertigstellen, parken oder abschließen, mit kurzer Begründung.",
    ],
    nonGoals: [
      "Keine Änderungen an Code, Konfiguration oder Abhängigkeiten.",
      "Keine neuen Features und keine Umbauten vorschlagen, die über „Fertig heißt“ hinausgehen.",
      "CI-Workflows unter .github/workflows ändert nur ein Mensch.",
    ],
    designNotes:
      "Ergebnis als docs/BESTANDSAUFNAHME.md im Repo mit den Abschnitten Stand, Fertig heißt, Später, Nächster Schritt und Empfehlung. Zuerst README, CLAUDE.md und vorhandene Pläne lesen, dann Tests und Build ausprobieren, soweit die Sandbox es erlaubt. Deutsch mit Umlauten, kurze Sätze.",
    acceptance: [
      "docs/BESTANDSAUFNAHME.md enthält die Abschnitte Stand, Fertig heißt, Später, Nächster Schritt und Empfehlung.",
      "Jede Aussage zum Stand nennt eine Datei, einen Befehl oder ein Testergebnis als Beleg.",
      "„Fertig heißt“ hat drei bis sieben Kriterien, jedes ist prüfbar formuliert.",
      "Im Repo ist außer docs/BESTANDSAUFNAHME.md nichts geändert.",
    ],
  };
}
